package com.bikompanion.ui.composables

import org.maplibre.spatialk.geojson.Position
import com.bikompanion.domain.BikeNetwork
import com.bikompanion.domain.BoundingBox

/**
 * Whether a network has anything inside the viewport worth fetching.
 *
 * Its real service area where the source publishes one. The old test — the network's single
 * centre point falling inside the box — is what makes a metro-wide operator flicker in and
 * out: AMBici's docks ring Barcelona across a dozen municipalities, and looking straight at
 * a row of them in Cornella left its centre, out past Badalona, off screen and the network
 * unpolled.
 *
 * Falls back to the centre point for sources that describe a network by one coordinate.
 */
internal fun BikeNetwork.serves(bbox: BoundingBox): Boolean {
    val area = coverage ?: return latitude in bbox.south..bbox.north &&
        longitude in bbox.west..bbox.east
    // The bounding box first, which rejects a distant city in four comparisons. It is too
    // generous on its own — the rectangle around AMBici reaches well into Bicing-only Gracia,
    // where AMBici has no dock at all — so the docks themselves settle it.
    if (!area.bounds.intersects(bbox)) return false
    return area.points.any { it.reaches(bbox) }
}

/**
 * Rough distance from the camera, used only to order networks by how central they are.
 *
 * Raw degrees rather than anything geodesic: over a single viewport the distortion can't
 * reorder two networks by enough to matter, and this runs on every camera settle.
 */
internal fun BikeNetwork.distanceSquaredTo(target: Position): Double {
    val dLat = latitude - target.latitude
    val dLon = longitude - target.longitude
    return dLat * dLat + dLon * dLon
}
